import {
  findMaxPriceBasedOnIncome,
  limitMaxPriceBasedOnDebts,
  limitMaxPriceBasedOnDownPayment,
} from './calculatorLogic'

export type GetMaxPurchasePriceParams = {
  annualIncome: number
  downPayment: number
  monthlyPersonalPayments: number
  borrowerCreditRating: 'good' | 'bad'
  amortization?: number
  qualifyingRate?: number
}

export enum QualifyingIssues {
  DebtTooHigh = 'debtTooHigh',
  DownPaymentTooLow = 'downPaymentTooLow',
  IncomeTooLow = 'incomeTooLow',
}

export type PurchasePriceResult = {
  maximumPurchasePrice: number
  qualifyingIssue1: QualifyingIssues | null
  qualifyingIssue2: QualifyingIssues | null
  error?: string
}

export const getMaximumPurchasePrice = (input: GetMaxPurchasePriceParams): PurchasePriceResult => {
  let incomeOnlyMaxPurchasePrice: number
  const data = {
    ...input,
    amortization: input.amortization || input.borrowerCreditRating === 'good' ? 25 : 30,
    qualifyingRate: input.qualifyingRate || input.borrowerCreditRating === 'good' ? 0.0534 : 0.065,
  }
  try {
    incomeOnlyMaxPurchasePrice = findMaxPriceBasedOnIncome(data)
  } catch (err) {
    return {
      maximumPurchasePrice: 0,
      qualifyingIssue1: null,
      qualifyingIssue2: null,
      error: err.message,
    }
  }

  let debtLimitedMaximumPurchasePrice: number
  try {
    debtLimitedMaximumPurchasePrice = limitMaxPriceBasedOnDebts(incomeOnlyMaxPurchasePrice, data)
  } catch (err) {
    return {
      maximumPurchasePrice: 0,
      qualifyingIssue1: null,
      qualifyingIssue2: null,
      error: err.message,
    }
  }

  let downPaymentLimitedMaximumPurchasePrice: number
  try {
    downPaymentLimitedMaximumPurchasePrice = limitMaxPriceBasedOnDownPayment(
      incomeOnlyMaxPurchasePrice,
      input,
    )
  } catch (err) {
    return {
      maximumPurchasePrice: 0,
      qualifyingIssue1: null,
      qualifyingIssue2: null,
      error: err.message,
    }
  }

  const maximumPurchasePrice = Math.min(
    debtLimitedMaximumPurchasePrice,
    downPaymentLimitedMaximumPurchasePrice,
  )
  const roundedMaxPurchasePrice = Math.max(
    0,
    Math.round(Math.ceil(maximumPurchasePrice / 1000)) * 1000,
  )

  const results = {
    maximumPurchasePrice: roundedMaxPurchasePrice,
    qualifyingIssue1: null as QualifyingIssues,
    qualifyingIssue2: null as QualifyingIssues,
  }

  // Figure out what is stopping them from qualifying for more
  const qualifyingIssues = [
    {
      key: QualifyingIssues.DebtTooHigh,
      value: debtLimitedMaximumPurchasePrice - incomeOnlyMaxPurchasePrice,
    },
    {
      key: QualifyingIssues.DownPaymentTooLow,
      value: downPaymentLimitedMaximumPurchasePrice - incomeOnlyMaxPurchasePrice,
    },
    { key: QualifyingIssues.IncomeTooLow, value: -1 },
  ]

  qualifyingIssues.sort((a, b) => {
    return a.value - b.value
  })

  results.qualifyingIssue1 = qualifyingIssues[0].key
  if (qualifyingIssues[1].value < -1) {
    results.qualifyingIssue2 = qualifyingIssues[1].key
  }

  return results
}
